import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, CircularProgress, Typography } from '@mui/material';
import CustomerConfirmationLayout from '../Components/CustomerConfirmationLayout';
import { formatOrgContactLine } from '../Shared/customerContact';
import { fetchPaymentConfirmation } from '../Shared/publicApi';

const PaymentSuccess = () => {
	const [params] = useSearchParams();
	const sessionId = (params.get('session_id') || '').trim();
	const [loading, setLoading] = useState(Boolean(sessionId));
	const [data, setData] = useState(null);

	useEffect(() => {
		if (!sessionId) {
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				const result = await fetchPaymentConfirmation(sessionId);
				if (!cancelled && result?.status !== 'pending') {
					setData(result);
				}
			} catch {
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [sessionId]);

	const headline = useMemo(() => {
		if (data?.payment_type === 'deposit') {
			return 'Deposit received';
		}
		return 'Payment received';
	}, [data?.payment_type]);

	const bodyCopy = useMemo(() => {
		if (!data) {
			return 'Thank you. Your payment was submitted successfully. You can close this page.';
		}

		const org = data.org_name || 'the contractor';
		const amount = data.amount_display;
		const job = data.job_title;

		if (data.payment_type === 'deposit' && amount && job) {
			return `Thank you. Your deposit of ${amount} for ${job} was submitted. ${org} will be in touch about next steps.`;
		}
		if (amount && job) {
			return `Thank you. Your payment of ${amount} for ${job} was submitted successfully. ${org} has been notified. You can close this page.`;
		}
		return `Thank you. Your payment was submitted successfully. ${org} has been notified. You can close this page.`;
	}, [data]);

	const contactLine = useMemo(
		() =>
			data
				? formatOrgContactLine({
						orgName: data.org_name,
						orgPhone: data.org_phone,
						orgEmail: data.org_email,
					})
				: null,
		[data],
	);

	if (loading) {
		return (
			<CustomerConfirmationLayout
				headline="Confirming your payment…"
				icon={<CircularProgress size={40} sx={{ color: 'primary.main' }} />}
			>
				<Typography color="text.secondary">
					This usually takes just a moment.
				</Typography>
			</CustomerConfirmationLayout>
		);
	}

	return (
		<CustomerConfirmationLayout
			orgLogoUrl={data?.org_logo_url}
			orgName={data?.org_name}
			headline={headline}
			icon={
				<CheckCircleOutlineIcon
					sx={{ fontSize: 56, color: 'success.main' }}
					aria-hidden
				/>
			}
		>
			{data?.amount_display && (
				<Typography
					variant="h4"
					component="p"
					sx={{ mb: 1.5, fontWeight: 700, color: 'primary.main' }}
				>
					{data.amount_display}
				</Typography>
			)}
			{data?.job_title && (
				<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
					{data.job_title}
				</Typography>
			)}
			<Typography color="text.secondary" sx={{ mb: contactLine ? 2 : 0 }}>
				{bodyCopy}
			</Typography>
			{contactLine && (
				<Box sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{contactLine}
					</Typography>
				</Box>
			)}
		</CustomerConfirmationLayout>
	);
};

export default PaymentSuccess;
